import React from 'react';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../lib/routing/frontend';
import { consultantDisplayName, invoiceAmountFormatter, niceDateTimeFormatter } from '../../lib/helper/formatting';

/**
 * MED-1002 - what was withheld, from whom, and on whose invoice.
 *
 * Every column here answers a question somebody has to act on: which consultant's bill this is,
 * which human was not told, which invoice, and how much. The links to the invoice and the patient
 * record are the two places the decision actually gets worked.
 *
 * ## Why the figures come off this row and not the invoice
 *
 * `suppressedAmount` is a snapshot in pence, taken when the notification was withheld, and it is
 * the figure the admin address was emailed. Rendering today's invoice amount instead would put
 * this screen out of step with the message somebody acted on.
 *
 * ## An amber row, not a red one
 *
 * A suppressed shortfall is a deliberate decision, not an error - but it is a patient who does
 * not know they owe money, so it should not read as routine either.
 */
class SuppressedShortfallListTable extends React.Component {
  renderPatient(suppression) {
    const { patient } = suppression;

    if (!patient) {
      return <span className="text-muted">Unknown</span>;
    }

    const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');

    return (
      <Link to={ROUTES.PATIENTS.SINGLE.replace(':id', patient.id)} className="text-body">
        {name || <span className="text-muted">-</span>}
      </Link>
    );
  }

  render() {
    const { suppressedShortfalls } = this.props;

    return (
      <div>
        <Table striped hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Date Suppressed</th>
              <th>Consultant</th>
              <th>Patient</th>
              <th>Invoice</th>
              <th className="text-right">Amount</th>
              <th>Notified</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {suppressedShortfalls.map((suppression) => (
              <tr key={suppression.id}>
                <td>{niceDateTimeFormatter(suppression.createdAt)}</td>
                <td>{suppression.consultant ? consultantDisplayName(suppression.consultant) : <span className="text-muted">Unknown</span>}</td>
                <td>{this.renderPatient(suppression)}</td>
                <td>
                  {suppression.invoice ? (
                    <Link to={ROUTES.INVOICES.SINGLE.replace(':id', suppression.invoice.id)} className="text-body">
                      {suppression.invoice.invoiceNo}
                    </Link>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="text-right">{invoiceAmountFormatter(suppression.suppressedAmount)}</td>
                <td>
                  {/*
                    A suppression with no address configured is still recorded, and this is where
                    that shows. "Withheld, and nobody was told" is worse than the ordinary case,
                    so it is called out rather than rendered as a blank cell.
                  */}
                  {suppression.adminEmail || <span className="text-danger">Nobody - no admin address configured</span>}
                </td>
                <td className="text-sm-left text-md-right">
                  {suppression.invoice && (
                    <Link to={ROUTES.INVOICES.SINGLE.replace(':id', suppression.invoice.id)} className="btn btn-sm btn-primary ml-2">
                      View Invoice
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }
}

export default SuppressedShortfallListTable;
